/**
 * Select Menu Global Sort Order Utility
 */

import { SelectOptions } from '../types';

/**
 * Maps semester names to their corresponding month numbers for chronological sorting.
 */
const SEMESTER_MONTH_MAP: Record<string, number> = {
  Spring: 0, // January
  Summer: 5, // June
  Fall: 8, // September
  Winter: 11, // December
};

/**
 * Converts a "Semester Year" string to a Date object for sorting.
 *
 * @param dateString - String in format "Semester Year" (e.g., "Fall 2024")
 * @returns Date object representing the start of that semester
 */
const getDateFromSemesterString = (dateString: string): Date => {
  const [semester, year] = dateString.split(' ');
  const month = SEMESTER_MONTH_MAP[semester] ?? 0;
  return new Date(parseInt(year), month, 1);
};

/**
 * Sorts semester options chronologically.
 *
 * @param menuOptions - Array of semester strings (e.g., ["Fall 2024", "Spring 2025"])
 * @returns New array sorted chronologically
 */
const sortSemesterOptions = (menuOptions: string[]): string[] => {
  return [...menuOptions].sort((a, b) => {
    const dateA = getDateFromSemesterString(a);
    const dateB = getDateFromSemesterString(b);
    return dateA.getTime() - dateB.getTime();
  });
};

/**
 * Applies global sorting strategies to specific select fields.
 *
 * Currently handles:
 * - `application_semester`: Sorts chronologically by semester/year
 *
 * Note: This function mutates the selectOptions array in place.
 *
 * @param selectOptions - Array of select options to process
 * @returns The same array with sorted options applied
 *
 * @example
 * handleSelectMenuSuperGlobalSortOrder(selectOptions);
 * // application_semester options now sorted: ["Spring 2024", "Fall 2024", "Spring 2025"]
 */
export const handleSelectMenuSuperGlobalSortOrder = (
  selectOptions: SelectOptions[],
): SelectOptions[] => {
  for (const option of selectOptions) {
    if (
      option.selection === 'application_semester' &&
      option.menuOptions.length > 0
    ) {
      option.menuOptions = sortSemesterOptions(option.menuOptions);
    }
  }
  return selectOptions;
};
